export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  setToken(token: string | null): void {
    this.accessToken = token;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await this.request(path, options);
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  async fetchBlob(path: string, options?: RequestInit): Promise<Blob> {
    const response = await this.request(path, options);
    return response.blob();
  }

  private async request(path: string, options?: RequestInit): Promise<Response> {
    const url = `/api${path}`;
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    };

    const tokenAttached = this.accessToken !== null;
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (options?.body) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, { ...options, headers, credentials: 'include' });

    if (this.shouldAttemptRefresh(response.status, path, tokenAttached)) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        const retry = await fetch(url, { ...options, headers, credentials: 'include' });
        if (!retry.ok) throw await this.toError(retry);
        return retry;
      }
      throw await this.toError(response);
    }

    if (!response.ok) throw await this.toError(response);
    return response;
  }

  private shouldAttemptRefresh(status: number, path: string, tokenAttached: boolean): boolean {
    if (path === '/auth/refresh' || path === '/auth/login') return false;
    if (status === 401) return true;
    if (status === 403 && tokenAttached) return true;
    return false;
  }

  async tryRefresh(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.doRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      this.accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    }
  }

  private async toError(response: Response): Promise<ApiError> {
    try {
      const body = (await response.json()) as { detail?: string; message?: string };
      return new ApiError(body.detail ?? body.message ?? 'Request failed', response.status, body);
    } catch {
      return new ApiError('Request failed', response.status);
    }
  }
}

export const api = new ApiClient();
